import React from "react";
import { ImageIcon } from "lucide-react";
import { Badge } from "../../../ui/badge";

// Dialogue Library v26.17 personalized images (aris-ai plan 44): one square master per
// variant (no person, F/M × 18–29 / 30–59 / 60+, general context), each with 3 crops.
export const isVariantKind = (kind) => String(kind || "").startsWith("variant_");

export const assetLabel = (asset) => {
  if (!asset) return "";
  if (asset.kind === "banner") return "Banner (current)";
  return asset.label || asset.kind;
};

/** live → "Live", a candidate → "Candidate", else "Not generated". */
export const assetStatus = (asset) => {
  if (asset?.regeneration?.state === "running") return "Generating";
  if (asset?.live) return "Live";
  if (asset?.candidate) return asset.candidate.status === "failed" ? "Failed" : "Candidate";
  return "Not generated";
};

const STATUS_VARIANTS = {
  Live: "success",
  Candidate: "warning",
  Failed: "destructive",
  Generating: "outline",
  "Not generated": "outline",
};

const thumbUrl = (asset) => asset?.live?.url || asset?.candidate?.url || null;

/** Every image of the Dialogue as a selectable tile. */
export const VariantPicker = ({ assets, selected, onSelect }) => (
  <div
    className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5"
    role="group"
    aria-label="Images of this dialogue"
  >
    {assets.map((asset) => {
      const url = thumbUrl(asset);
      const status = assetStatus(asset);
      const active = asset.kind === selected;
      return (
        <button
          key={asset.kind}
          type="button"
          aria-pressed={active}
          aria-label={`${assetLabel(asset)} — ${status}`}
          onClick={() => onSelect(asset.kind)}
          className={`space-y-1 rounded-lg border p-1 text-left transition ${
            active ? "border-primary ring-2 ring-primary/40" : "hover:border-primary/50"
          }`}
        >
          <div
            className={`w-full overflow-hidden rounded-md bg-muted/40 ${
              isVariantKind(asset.kind) ? "aspect-square" : "aspect-video"
            }`}
          >
            {url ? (
              <img src={url} alt="" className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                <ImageIcon className="h-5 w-5" />
              </div>
            )}
          </div>
          <div className="flex items-center justify-between gap-1 px-1">
            <span className="truncate text-xs font-medium">{assetLabel(asset)}</span>
            <Badge variant={STATUS_VARIANTS[status] || "outline"} className="text-[10px]">
              {status}
            </Badge>
          </div>
        </button>
      );
    })}
  </div>
);

// Shown at the size each surface displays them (the files are 2x).
const SURFACES = [
  { key: "recommended", label: "Recommended", w: 120, h: 138 },
  { key: "overview", label: "Overview", w: 188, h: 140 },
  { key: "active", label: "Active", w: 120, h: 50 },
];

/** A variant's three crops, in their real card proportions. */
export const CropStrip = ({ crops, title }) => {
  if (!crops) return null;
  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-muted-foreground">{title}</p>
      <div className="flex flex-wrap items-end gap-3">
        {SURFACES.map(({ key, label, w, h }) =>
          crops[key]?.url ? (
            <figure key={key} className="space-y-1">
              <img
                src={crops[key].url}
                alt={`${label} crop`}
                width={w}
                height={h}
                className="rounded-md border object-cover"
                style={{ width: w, height: h }}
              />
              <figcaption className="text-[10px] text-muted-foreground">{label}</figcaption>
            </figure>
          ) : null
        )}
      </div>
    </div>
  );
};
